import React from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { Button, space, Card, color, fontWeight, H3 } from '@ticketswap/solar'
import { useQuery, gql } from '@apollo/client'
import Link from 'next/link'
import { useViewer } from '~/components/ViewerContext'
import PricePerTicket from '~/components/PricePerTicket'
import { getLinkProps } from '~/utils'
import { decodeGlobalId } from '~/utils/id'
import { listingsFragment } from '~/graphql/fragments/listings'
import ListingAvatar from '~/components/ListingAvatar'
import ExternalPrimaryTicketShopOffering from '~/components/ExternalPrimaryTicketShopOffering'
import OrganizerOffering from '~/components/OrganizerOffering'
import { externalPrimaryTicketShopFragmentPropTypes } from '~/graphql/fragments/externalPrimaryTicketShop'
import { eventFragmentPropTypes } from '~/graphql/fragments/event'
import { eventTypeFragmentPropTypes } from '~/graphql/fragments/eventType'

export const StyledCard = styled(Card)`
  background-color: ${props => props.isOwnedByViewer && 'rgba(0, 206, 92, 0.08)'};
  opacity: ${props => props.transparent && '0.5'};

  &:hover {
    background-color: ${props => props.isOwnedByViewer && color.titanLightestAlpha};
  }

  header,
  footer {
    align-self: flex-start;
  }
`

export const GET_AVAILABLE_LISTINGS = gql`
  query getAvailableListings($id: ID!, $first: Int, $after: String) {
    node(id: $id) {
      ... on EventType {
        id
        slug
        title
        availableListings: listings(
          first: $first
          filter: { listingStatus: AVAILABLE }
          after: $after
        ) {
          ...listings
        }
      }
    }
  }
  ${listingsFragment}
`

const ListItem = styled.li`
  margin-top: ${space[8]};
`

const StyledButton = styled(Button)`
  margin-top: ${space[8]};
`

const Description = styled.span`
  opacity: 0.6;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Title = styled(H3)`
  font-weight: ${fontWeight.semiBold};
`

const Available = ({
  eventTypeId,
  resultsPerPage,
  t,
  externalPrimaryTicketShop,
  showExternalPrimaryTicketShop,
  event,
  eventType,
  hasOrganizerShop,
  organizerShopUrl,
}) => {
  const { viewer } = useViewer()
  const { data = {}, loading, fetchMore } = useQuery(GET_AVAILABLE_LISTINGS, {
    notifyOnNetworkStatusChange: true,
    variables: { id: eventTypeId, first: resultsPerPage },
  })
  const { availableListings } = data.node || {}
  const hasListings = Boolean(availableListings?.edges?.length > 0)
  const showTitle = showExternalPrimaryTicketShop || hasOrganizerShop || hasListings

  return (
    <>
      {showTitle && <Title>{t('event-eventtype:tickets.available')}</Title>}

      {!hasOrganizerShop && showExternalPrimaryTicketShop && (
        <ExternalPrimaryTicketShopOffering
          t={t}
          externalPrimaryTicketShop={externalPrimaryTicketShop}
        />
      )}

      {hasOrganizerShop && (
        <OrganizerOffering t={t} event={event} eventType={eventType} url={organizerShopUrl} />
      )}

      {hasListings && (
        <ul data-testid="available-tickets-list">
          {availableListings.edges.map(listing => {
            const { description, id, hash, event, eventType, price, seller, tickets } = listing.node
            const isOwnedByViewer = Boolean(viewer?.id === seller.id)
            const availableTicketsInListing = tickets.edges.filter(
              ({ node: { status } }) => status === 'AVAILABLE'
            ).length

            return (
              <ListItem key={id}>
                <Link
                  {...getLinkProps('LISTING_PAGE', {
                    slug: event.slug,
                    id: decodeGlobalId(id),
                    hash: hash,
                  })}
                  passHref
                >
                  <a>
                    <StyledCard
                      isOwnedByViewer={isOwnedByViewer}
                      size="large"
                      verticalAlign="top"
                      leftAdornment={<ListingAvatar image={seller.avatar} />}
                      title={t('event-eventtype:tickets.listingTitle', {
                        count: availableTicketsInListing,
                      })}
                      subtitle={eventType.title}
                      description={description && <Description>"{description}"</Description>}
                      rightAdornment={
                        <PricePerTicket t={t} price={price.totalPriceWithTransactionFee} />
                      }
                    />
                  </a>
                </Link>
              </ListItem>
            )
          })}
        </ul>
      )}

      {availableListings?.pageInfo.hasNextPage && (
        <StyledButton
          variant="secondary"
          fullWidth
          loading={loading}
          onClick={() => {
            fetchMore({
              variables: {
                after: availableListings.pageInfo.endCursor,
              },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                const newEdges = fetchMoreResult.node.availableListings.edges
                const pageInfo = fetchMoreResult.node.availableListings.pageInfo

                return newEdges.length > 0
                  ? {
                      ...fetchMoreResult,
                      node: {
                        ...fetchMoreResult.node,
                        availableListings: {
                          ...fetchMoreResult.node.availableListings,
                          edges: [
                            ...previousResult.node.availableListings.edges,
                            ...fetchMoreResult.node.availableListings.edges,
                          ],
                          pageInfo,
                        },
                      },
                    }
                  : previousResult
              },
            })
          }}
        >
          {t('common:generic.showMore')}
        </StyledButton>
      )}
    </>
  )
}

Available.defaultProps = {
  resultsPerPage: 50,
}

Available.propTypes = {
  eventTypeId: PropTypes.string,
  resultsPerPage: PropTypes.number,
  t: PropTypes.func.isRequired,
  showExternalPrimaryTicketShop: PropTypes.bool.isRequired,
  externalPrimaryTicketShop: externalPrimaryTicketShopFragmentPropTypes,
  event: eventFragmentPropTypes.isRequired,
  eventType: eventTypeFragmentPropTypes.isRequired,
  hasOrganizerShop: PropTypes.bool,
  organizerShopUrl: PropTypes.string,
}

export default Available
